import React, { Dispatch, SetStateAction } from "react";
import Pagination from "rc-pagination";
import { locale } from "../../common/table/paginatorConfig";
import { SortIcons } from "components/SortIcons/SortIcons";
import { IPageResponse, SortDirection } from "common/interfaces/IPageResponse";
import { Button, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { ELECTRICITY_AGREEMENT_RENEWAL_PLAN_STATUS_MAP, ELECTRICITY_AGREEMENT_RENEWAL_PLAN_TYPE_MAP, ElectricityAgreementRenewalPlan, ElectricityAgreementRenewalPlanSortFields, ElectricityAgreementRenewalPlanStatus, ElectricityAgreementRenewalPlanType } from "./models";
import { MeteringPointCell } from "components/MeteringPointCell/MeteringPointCell";
import { dateFormatOptions } from "common/dateFormatterConfig";

interface ElectricityAgreementRenewalPlansTableProps {
  renewalPlans?: IPageResponse<ElectricityAgreementRenewalPlan>;
  onPageChange: (page: number, size: number) => void;
  onSort: (sortField: ElectricityAgreementRenewalPlanSortFields) => void;
  sortField: ElectricityAgreementRenewalPlanSortFields;
  sortDirection: SortDirection;
  isRequesting: boolean;
  updateAgreementRenewalPlan: (plan: ElectricityAgreementRenewalPlan) => void;
  cancelAgreementRenewalPlan: (plan: ElectricityAgreementRenewalPlan) => void;
  selectedRenewalPlanIds: number[];
  setSelectedRenewalPlanIds: Dispatch<SetStateAction<number[]>>;
  excludedRenewalPlanIds: number[];
  setExcludedRenewalPlanIds: Dispatch<SetStateAction<number[]>>;
  onRenewalPlanSelect: (plan: ElectricityAgreementRenewalPlan, checked: boolean) => void;
  allRenewalPlansSelected: boolean;
  setAllRenewalPlansSelected: Dispatch<SetStateAction<boolean>>;
  showSelectAll: boolean;
}

export function ElectricityAgreementRenewalPlansTable(props: ElectricityAgreementRenewalPlansTableProps) {
    const renewalPlans: Array<ElectricityAgreementRenewalPlan> = props.renewalPlans ? props.renewalPlans.content : [];
    const pageResponse = props.renewalPlans;

    const reportTableRows = renewalPlans.map((renewalPlan) => {
    const showActions = ( renewalPlan.status === ElectricityAgreementRenewalPlanStatus.PLANNED || 
                        renewalPlan.status === ElectricityAgreementRenewalPlanStatus.INCOMPLETE 
                        ) && renewalPlan.type === ElectricityAgreementRenewalPlanType.RENEWAL;

        return (
            <tr key={'row-' + renewalPlan.id} className={renewalPlan.status === ElectricityAgreementRenewalPlanStatus.INCOMPLETE ? 'incomplete-plan' : ''}>
                {
                    showActions ? (
                        <td className="text-center p-0">
                            <label htmlFor={`renewal-plan-check-${renewalPlan.id}`} className="m-0 px-3 cursor-pointer-hover">
                                <input 
                                    id={`renewal-plan-check-${renewalPlan.id}`} 
                                    className="cursor-pointer-hover"
                                    checked={(props.allRenewalPlansSelected && !props.excludedRenewalPlanIds.find(plan => plan === renewalPlan.id)) || props.selectedRenewalPlanIds.some((id) => id === renewalPlan.id)}
                                    type="checkbox" 
                                    onChange={(event => {
                                        props.onRenewalPlanSelect(renewalPlan, event.target.checked);
                                    })}
                                />
                            </label>
                        </td>
                    ) : <td></td>
                }
                <td className="text-nowrap">
                    {renewalPlan.id}<br />
                </td>
                <td className="line-height-1">
                    {renewalPlan.contractNumber}<br />
                </td>
                <td className="line-height-1">
                    {renewalPlan.customer.name}<br />
                </td>
                <td className="text-nowrap">
                    {renewalPlan.customer.isChargingClient ? "Jah" : "Ei"}
                </td>
                <td>
                    <MeteringPointCell meteringPoints={renewalPlan.meteringPoints} id={renewalPlan.id} />
                </td>
                <td className="text-nowrap renewal-plan-status">
                    <StatusLabel status={renewalPlan.status} />
                </td>
                <td className="text-nowrap">
                    {renewalPlan.offerSendDate ? new Intl.DateTimeFormat('et', dateFormatOptions).format(new Date(renewalPlan.offerSendDate)) : ""}
                </td>
                <td className="text-nowrap text-left">
                    { ELECTRICITY_AGREEMENT_RENEWAL_PLAN_TYPE_MAP[renewalPlan.type] }
                </td>
                <td>
                    <div className="row__actions">
                        {
                            showActions && (
                                <>
                                    <OverlayTrigger
                                        delay={250}
                                        overlay={
                                            <Tooltip id={`update-package-tooltip-${renewalPlan.id}`}>
                                                <p style={{margin: '3px'}}>Muuda plaani</p>
                                            </Tooltip>
                                        } 
                                            placement={"left"}
                                    >
                                        <Button 
                                            size="sm"                             
                                            variant="secondary"
                                            className="d-flex align-items-center btn-outline-icon" 
                                            onClick={() => props.updateAgreementRenewalPlan(renewalPlan)}
                                            >
                                                <i className="icon--16 my-2 icon__edit--outline-mask"/>
                                        </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger
                                        delay={250}
                                        overlay={
                                            <Tooltip id={`update-package-tooltip-${renewalPlan.id}`}>
                                                <p style={{margin: '3px'}}>Tühista plaan</p>
                                            </Tooltip>
                                        } 
                                        placement={"left"}
                                    >
                                        <Button 
                                            size="sm"                             
                                            variant="secondary"
                                            className="d-flex align-items-center btn-outline-icon" 
                                            onClick={() => props.cancelAgreementRenewalPlan(renewalPlan)}
                                            >
                                                <i className="icon--16 my-2 icon__cancel--outline-mask"/>
                                        </Button>
                                    </OverlayTrigger>
                                </>
                            )
                        }
                    </div>
                </td>
            </tr>
        )
        }
    );

    return (
        <table className='generic-table table table-responsive-md table-hover'>
            <thead>
                <tr>
                    <th className="text-center p-0">
                        {
                            props.showSelectAll ? (
                                <label htmlFor="renewal-plan-check-all" className="m-0 px-3 cursor-pointer-hover">
                                    <input 
                                        id="renewal-plan-check-all" 
                                        className="cursor-pointer-hover"
                                        checked={props.allRenewalPlansSelected}
                                        type="checkbox" 
                                        onChange={((e) => {
                                            if(e.target.checked) {
                                                props.setAllRenewalPlansSelected((allSelected) => !allSelected)
                                                props.setSelectedRenewalPlanIds([]);
                                            } else {
                                                props.setAllRenewalPlansSelected((allSelected) => !allSelected)
                                                props.setExcludedRenewalPlanIds([]);
                                            }
                                        })}
                                    />
                                </label>
                            ) : null
                        }
                    </th>
                    <th className="sort-down-up-hover">
                        <div className="d-flex align-items-center"
                            onClick={() => props.onSort(ElectricityAgreementRenewalPlanSortFields.ID)}>
                            <span>ID</span>
                            <SortIcons
                                sortedByField={props.sortField}
                                sortDirection={props.sortDirection}
                                currentSortColumn={ElectricityAgreementRenewalPlanSortFields.ID} />
                        </div>
                    </th>
                    <th>Lepingu number</th>
                    <th>Klient</th>
                    <th>Laadimisklient</th>
                    <th>Mõõtepunkt(id)</th>
                    <th>Staatus</th>
                    <th>Saatmise kuupäev</th>
                    <th className="text-left">Tüüp</th>
                </tr>
            </thead>
            <tbody>
                {
                    props.isRequesting ?
                    <tr>
                        <td colSpan={11} className="spinner-cell">
                            <div className="p-4 w-100 d-flex align-items-center justify-content-center">
                                <Spinner animation="grow" />
                            </div>
                        </td>
                    </tr> : reportTableRows
                }
            </tbody>
            {
                pageResponse ? (
                    <tfoot>
                        <tr>
                            <td colSpan={11}>
                                <div className="pagination-wrapper">
                                    <Pagination
                                        locale={locale}
                                        current={pageResponse.number + 1}
                                        total={pageResponse.totalElements}
                                        pageSize={pageResponse.size}
                                        prefixCls={"pagination"}
                                        onChange={props.onPageChange}
                                        showSizeChanger={false}
                                        hideOnSinglePage={true}
                                    />
                                </div>
                            </td>
                        </tr>
                    </tfoot>
                ) : null
            }
        </table>
    )
}

const STATUS_CLASS_MAP: Record<ElectricityAgreementRenewalPlanStatus, string> = {
    PLANNED: 'planned',
    INCOMPLETE: 'incomplete',
    PENDING: 'pending',
    CANCELED: 'canceled',
    ACCEPTED: 'accepted',
    REJECTED: 'rejected',
    AUTO_ACCEPTED: 'auto-accepted'
};

const StatusLabel = ({ status }: { status: ElectricityAgreementRenewalPlanStatus }) => {
    const className = STATUS_CLASS_MAP[status] || 'planned';

    return (
        <div className={className}>
            {ELECTRICITY_AGREEMENT_RENEWAL_PLAN_STATUS_MAP[status]}
        </div>
    );
};